<template>
  <nav
    id="navbar"
    class="fixed top-0 z-40 flex w-full flex-row items-center bg-parrot px-4 justify-between border-b border-gray-200"
  >
    <div
      class="sticky justify-center top-0 z-40 flex h-12 shrink-0 items-center gap-x-4 bg-parrot px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8"
    >
      <button
        type="button"
        class="-m-2.5 p-2.5 text-parrot-1 lg:hidden"
        @click="sidebarOpen = true"
      >
        <span class="sr-only">Abrir menu</span>
        <Bars3Icon class="h-6 w-6" aria-hidden="true" />
      </button>
      <div class="hidden lg:block">
        <img class="w-auto h-10" alt="Avexo logo" src="./assets/avexo.png" />
      </div>
    </div>
    <ul class="flex-row items-center py-1 text-lg text-white sm:flex">
      <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        <div class="flex items-center gap-x-4 lg:gap-x-6">
          <!-- Profile dropdown -->
          <Menu as="div" class="relative">
            <!-- Loading skeleton for profile dropdown -->
            <div
              v-if="isLoadingApp || !resaleSelected.name"
              class="flex items-center my-2"
            >
              <div
                class="w-16 h-8 mr-2 p-1.5 border-parrot-1 border rounded-md bg-white bg-opacity-20 animate-pulse flex-shrink-0"
              ></div>
              <div
                class="flex items-center p-1.5 border-parrot-1 border rounded-md w-44 lg:w-52 animate-pulse"
              >
                <!-- Skeleton para logo -->
                <div
                  class="w-10 h-10 mr-2 rounded bg-white bg-opacity-20 animate-pulse flex-shrink-0"
                ></div>
                <!-- Skeleton para informações -->
                <div class="flex flex-col flex-1">
                  <span class="flex items-center">
                    <div class="h-4 bg-white bg-opacity-30 rounded w-24"></div>
                  </span>
                  <span class="flex items-center mt-1">
                    <div class="h-3 bg-white bg-opacity-20 rounded w-32"></div>
                  </span>
                </div>
              </div>
            </div>

            <!-- Actual profile dropdown -->
            <div v-else class="flex items-center my-2">
              <button
                type="button"
                class="inline-flex items-center bg-parrot-2 text-white text-sm px-3 py-1.5 rounded-md hover:bg-opacity-90 transition-all duration-200 mr-2"
                @click="goToComprar"
              >
                Nova licença
              </button>
              <MenuButton
                class="flex items-center p-1.5 border-parrot-1 border rounded-md w-44 lg:w-52"
              >
                <span class="sr-only">Abrir empresas</span>
                <!-- Logo da revenda à esquerda -->
                <div v-if="resaleSelected.logo" class="mr-2 flex-shrink-0">
                  <img
                    :src="`data:image/png;base64,${resaleSelected.logo}`"
                    :alt="`Logo ${resaleSelected.name}`"
                    class="w-10 h-10 rounded object-cover pointer-events-none select-none"
                    @error="handleLogoError"
                  />
                </div>
                <!-- Informações da empresa à direita -->
                <div
                  :class="[
                    'flex flex-col flex-1 min-w-0',
                    !resaleSelected.logo
                      ? 'items-center justify-center text-center'
                      : '',
                  ]"
                >
                  <span class="flex items-center">
                    <span
                      class="text-sm font-semibold leading-6 text-white line-clamp-1 truncate"
                      aria-hidden="true"
                      >{{ resaleSelected.name }}</span
                    >
                    <ChevronDownIcon
                      v-if="resalesNavigation.length > 1"
                      class="ml-2 h-4 w-4 text-white flex-shrink-0"
                      aria-hidden="true"
                    />
                  </span>
                  <span class="flex items-center">
                    <span
                      class="text-sm leading-6 text-white truncate"
                      aria-hidden="true"
                      >{{ formatCNPJ(resaleSelected.cnpj) }}</span
                    >
                  </span>
                </div>
              </MenuButton>
              <div
                v-if="resaleSelected.categoria"
                class="absolute -bottom-2 -right-2 z-20 rounded-full bg-parrot p-0.5 shadow-md ring-1 ring-white/20"
              >
                <ResaleCategoryBadge :categoria="resaleSelected.categoria" />
              </div>
            </div>
            <transition
              v-if="!isLoadingApp && resaleSelected.name"
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                v-if="resalesNavigation.length > 1"
                class="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
              >
                <MenuItem
                  v-for="item in resalesNavigation"
                  :key="item.cnpj"
                  @click="item.action(item)"
                  class="flex flex-row space-x-2 hover:cursor-pointer"
                >
                  <span
                    :class="[
                      item.cnpj === resaleSelected.cnpj
                        ? 'bg-parrot text-white'
                        : '',
                      'block px-3 py-1 text-sm leading-6 text-parrot',
                    ]"
                  >
                    <span
                      v-if="item.cnpj === resaleSelected.cnpj"
                      :class="[
                        item.cnpj === resaleSelected.cnpj
                          ? 'text-white'
                          : 'text-parrot',
                        'inset-y-0 left-0 flex items-center',
                      ]"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                    {{ formatCNPJ(item.cnpj) }}
                  </span>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </ul>
  </nav>
  <!-- Navbar end -->

  <TransitionRoot as="template" :show="sidebarOpen">
    <Dialog class="relative z-50 lg:hidden" @close="sidebarOpen = false">
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900/80" />
      </TransitionChild>

      <div class="fixed inset-0 flex">
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div
                class="absolute left-full top-0 flex w-16 justify-center pt-5"
              >
                <button
                  type="button"
                  class="-m-2.5 p-2.5"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <!-- Sidebar component, swap this element with another sidebar if you like -->
            <div
              class="flex grow flex-col gap-y-5 overflow-y-auto bg-parrot px-4 py-10 pb-4"
            >
              <!-- Header do sidebar mobile com informações da empresa -->
              <div class="border-b border-white border-opacity-20 pb-4">
                <!-- Informações da empresa com logo -->
                <div
                  class="relative flex items-center p-3 border border-white border-opacity-20 rounded-lg"
                >
                  <!-- Logo da revenda no mobile -->
                  <div v-if="resaleSelected.logo" class="mr-4 flex-shrink-0">
                    <img
                      :src="`data:image/png;base64,${resaleSelected.logo}`"
                      :alt="`Logo ${resaleSelected.name}`"
                      class="w-14 h-14 rounded object-cover pointer-events-none select-none"
                      @error="handleLogoError"
                    />
                  </div>
                  <!-- Informações à direita -->
                  <div
                    :class="[
                      'flex flex-col flex-1 min-w-0 text-white',
                      !resaleSelected.logo
                        ? 'items-center justify-center text-center'
                        : '',
                    ]"
                  >
                    <div class="text-base font-semibold truncate">
                      {{ resaleSelected.name }}
                    </div>
                    <div class="text-sm opacity-75 truncate">
                      {{ formatCNPJ(resaleSelected.cnpj) }}
                    </div>
                  </div>
                  <div
                    v-if="resaleSelected.categoria"
                    class="absolute -bottom-2 -right-2 z-20 rounded-full bg-parrot p-0.5 shadow-md ring-1 ring-white/20"
                  >
                    <ResaleCategoryBadge
                      :categoria="resaleSelected.categoria"
                    />
                  </div>
                </div>
              </div>
              <nav class="flex flex-1 flex-col">
                <ul role="list" class="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" class="-mx-2 space-y-1">
                      <li v-for="item in navigation" :key="item.name">
                        <a
                          :href="item.href"
                          :class="[
                            isActive(item.href)
                              ? 'bg-white bg-opacity-5 text-parrot-1'
                              : 'text-white hover:text-parrot-1 hover:bg-white hover:bg-opacity-5',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                          ]"
                        >
                          <component
                            :is="item.icon"
                            :class="[
                              isActive(item.href)
                                ? 'text-parrot-1'
                                : 'text-white group-hover:text-parrot-1',
                              'h-6 w-6 shrink-0',
                            ]"
                            aria-hidden="true"
                          />
                          {{ item.name }}
                        </a>
                      </li>
                    </ul>
                  </li>
                  <li class="mt-auto">
                    <button
                      type="button"
                      class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 bg-parrot-2 text-white hover:bg-opacity-90 transition-all duration-200 mb-3 w-full justify-center"
                      @click="goToComprarAndClose"
                    >
                      Nova licença
                    </button>
                    <button
                      @click="logout()"
                      class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-white hover:bg-opacity-5 hover:text-parrot-1 w-full justify-center"
                    >
                      <ArrowLeftEndOnRectangleIcon
                        class="h-6 w-6 shrink-0 text-white group-hover:text-parrot-1"
                        aria-hidden="true"
                      />
                      Sair
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Sidebar starts -->
  <div
    class="hidden lg:fixed lg:inset-y-0 lg:z-30 lg:mt-16 lg:flex lg:w-60 lg:flex-col"
  >
    <!-- Sidebar component, swap this element with another sidebar if you like -->
    <div
      class="flex grow flex-col gap-y-5 overflow-y-auto bg-parrot px-4 py-10 pb-4"
    >
      <nav class="flex flex-1 flex-col">
        <ul role="list" class="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" class="-mx-2 space-y-1">
              <li v-for="item in navigation" :key="item.name">
                <a
                  :href="item.href"
                  :class="[
                    isActive(item.href)
                      ? 'bg-white bg-opacity-5 text-parrot-1'
                      : 'text-white hover:text-parrot-1 hover:bg-white hover:bg-opacity-5',
                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                  ]"
                >
                  <component
                    :is="item.icon"
                    :color="isActive(item.href) ? '#B2FF59' : 'white'"
                    :class="[
                      isActive(item.href)
                        ? 'text-parrot-1'
                        : 'text-white group-hover:text-parrot-1',
                      'h-6 w-6 shrink-0',
                    ]"
                    aria-hidden="true"
                  />
                  {{ item.name }}
                </a>
              </li>
            </ul>
          </li>
          <li class="mt-auto">
            <button
              @click="logout()"
              class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-white hover:bg-opacity-5 hover:text-parrot-1 w-full text-left"
            >
              <ArrowLeftEndOnRectangleIcon
                class="h-6 w-6 shrink-0 text-white group-hover:text-parrot-1"
                aria-hidden="true"
              />
              Sair
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <div class="lg:pl-60 pt-14 mx-auto">
    <main class="py-10">
      <div class="px-4 sm:px-6 lg:px-8">
        <ClientListSkeleton v-if="isLoadingApp && !hasInitiallyLoaded" />
        <router-view v-else />
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, provide, onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import ClientListSkeleton from "@/components/skeleton/client-list-skeleton.vue";
import {
  Bars3Icon,
  Cog6ToothIcon,
  UserCircleIcon,
  // LogoutIcon,
  ArrowLeftEndOnRectangleIcon,
  CheckIcon,
  MapPinIcon,
} from "@heroicons/vue/24/outline";
import {
  ChevronDownIcon,
  ChartPieIcon,
  // QuestionMarkCircleIcon,
} from "@heroicons/vue/20/solid";

import SaleIcon from "@/components/icons/sale-icon";
import KeycloakService from "@/security/keycloak-service";
import HttpService from "@/services/http-service";
import ReceiptIcon from "@/components/icons/receipt-icon";
import DashboardIcon from "@/components/icons/dashboard-icon";
import ResaleCategoryBadge from "@/components/resale-category-badge.vue";

const httpService = HttpService.getAxiosClient();

const navigation = [
  {
    name: "Início",
    href: "/",
    icon: DashboardIcon,
  },
  { name: "Clientes", href: "/clientes", icon: SaleIcon },
  { name: "Mapa", href: "/clientes/mapa", icon: MapPinIcon },
  { name: "Cobranças", href: "/cobrancas", icon: ReceiptIcon },
  {
    name: "Recebimentos",
    href: "/financeiro",
    icon: ChartPieIcon,
  },
  { name: "Perfil", href: "/perfil", icon: UserCircleIcon },
  {
    name: "Configurações",
    href: "/financeiro/configuracao",
    icon: Cog6ToothIcon,
  },
];

const route = useRoute();
const router = useRouter();

const isActive = (href) => {
  return route.path === href;
};

const resalesNavigation = ref([]);
const resaleSelected = ref<any>({
  name: "",
  cnpj: "",
  logo: "",
  categoria: null,
});

// lista de níveis (com mínimo de clientes) vinda de /resales, para derivar o
// nível pela quantidade de clientes ativos — igual à tela de Perfil.
const categoriasList = ref<any[]>([]);
const categoriaPorClientes = (resale: any) => {
  const ord = [...categoriasList.value].sort(
    (a, b) =>
      (a.quantidade_minima_clientes || 0) - (b.quantidade_minima_clientes || 0)
  );
  let atual: any = null;
  for (const c of ord) {
    if ((resale?.clientes_ativos ?? 0) >= (c.quantidade_minima_clientes || 0))
      atual = c;
  }
  return atual || resale?.categoria || null;
};
const isLoadingApp = ref(true);
const hasInitiallyLoaded = ref(false);

const profile = ref();

provide("profile", profile);

provide(
  "tenant",
  computed(() => resaleSelected)
);

const getProfileInfo = async () => {
  try {
    isLoadingApp.value = true;
    const { data } = await httpService.get(`/resales`);
    const resales = data.resales;
    categoriasList.value = data.categorias || [];

    if (window.localStorage.getItem("cnpj")) {
      const selectedResale = resales.find(
        (resale) => resale.cnpj === window.localStorage.getItem("cnpj")
      );
      if (selectedResale) {
        resaleSelected.value = {
          name: selectedResale.razao_social,
          cnpj: selectedResale.cnpj,
          logo: selectedResale.logo || "",
          categoria: categoriaPorClientes(selectedResale),
        };
      }
    }

    resalesNavigation.value = resales.map((resale) => ({
      name: resale.razao_social,
      cnpj: resale.cnpj,
      logo: resale.logo || "",
      categoria: categoriaPorClientes(resale),
      action: (item) => changeResale(item),
    }));

    window.localStorage.setItem("cnpj", resales[0].cnpj);

    if (resales.length === 1) {
      window.localStorage.setItem("cnpj", resales[0].cnpj);
      resaleSelected.value = {
        name: resales[0].razao_social,
        cnpj: resales[0].cnpj,
        logo: resales[0].logo || "",
        categoria: categoriaPorClientes(resales[0]),
        action: (item) => changeResale(item),
      };
    }
  } catch (error) {
    console.log(error);
  } finally {
    isLoadingApp.value = false;
    hasInitiallyLoaded.value = true;
  }
};

const changeResale = async (resale: {
  name: string;
  cnpj: string;
  logo?: string;
  categoria?: any;
}) => {
  resaleSelected.value = {
    name: resale.name,
    cnpj: resale.cnpj,
    logo: resale.logo || "",
    categoria: resale.categoria || null,
  };
  window.localStorage.setItem("cnpj", resale.cnpj);
};

const handleLogoError = (event: Event) => {
  // Ocultar a imagem se houver erro no carregamento
  const img = event.target as HTMLImageElement;
  if (img) {
    img.style.display = "none";
  }
};

function formatCNPJ(cnpj: string): string {
  if (!cnpj) return "";
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
}

const logout = () => {
  KeycloakService.callLogOut();
  window.localStorage.clear();
};

const goToComprar = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  router.push({ path: "/comprar" });
};

const goToComprarAndClose = () => {
  sidebarOpen.value = false;
  goToComprar();
};

// Atualiza o logo no topo/menu quando o Perfil salva/remove (sem reload)
const handleLogoUpdated = (event: Event) => {
  const detail = (event as CustomEvent).detail || {};
  const { cnpj, logo } = detail;
  if (!cnpj) return;

  if (resaleSelected.value.cnpj === cnpj) {
    resaleSelected.value = { ...resaleSelected.value, logo: logo || "" };
  }
  const idx = resalesNavigation.value.findIndex((r: any) => r.cnpj === cnpj);
  if (idx !== -1) {
    resalesNavigation.value[idx] = {
      ...resalesNavigation.value[idx],
      logo: logo || "",
    };
  }
};

onMounted(async () => {
  window.addEventListener("resale-logo-updated", handleLogoUpdated);
  await getProfileInfo();
});

onUnmounted(() => {
  window.removeEventListener("resale-logo-updated", handleLogoUpdated);
});

const sidebarOpen = ref(false);
</script>
